import { arrayMove } from '@dnd-kit/sortable'
import {
  AddIcon,
  FormInput,
  Modal,
  ModalSizeEnum,
  PrimaryButton,
  SecondaryButton,
} from '@systemeio/ui'
import { StageItem } from 'modules/crm/components/stage-item'
import { PipelineErrorsInterface } from 'modules/crm/pipeline/type/pipeline-errors-interface'
import {
  PipelineCreateStageInterface,
  PipelineEditInterface,
} from 'modules/crm/types/pipeline-interface'
import React, { Dispatch, SetStateAction } from 'react'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import Sortable from 'shared/components/sortable'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface PipelineUpdateBodyProps {
  tempState: PipelineEditInterface
  setTempState: Dispatch<SetStateAction<PipelineEditInterface>>
  errors: PipelineErrorsInterface
  isUpdate: boolean
  isOpened: boolean
  onClose: () => void
  afterLeave: () => void
  saveCrmPipeline: () => void
  modalTitle: string
  withConfirmation?: boolean
}

export const PipelineUpdateBody = ({
  tempState,
  setTempState,
  errors,
  withConfirmation,
  isUpdate,
  onClose,
  afterLeave,
  saveCrmPipeline,
  modalTitle,
  isOpened,
}: PipelineUpdateBodyProps) => {
  const { t } = useLocoTranslation()

  const createNewStage = () => {
    const lastItem = tempState.stages.at(-1)
    if (lastItem) {
      setTempState(prevState => ({
        ...prevState,
        stages: [...prevState.stages, { name: '', position: lastItem.position + 1 }],
      }))
    }
  }

  const getStageItem = (stage: PipelineCreateStageInterface, index?: number) => (
    <StageItem
      stage={stage}
      setTempState={setTempState}
      key={`stage-${stage.position}`}
      showBin={tempState.stages.length > 2}
      withConfirmation={withConfirmation}
      error={
        index
          ? errors.fields[`stages[${index}]`] || errors.fields[`stages[${index}].name`]
          : undefined
      }
    />
  )

  const onMove = async (activeIndex: number, overIndex: number) => {
    const reorderedStages = arrayMove(tempState.stages, activeIndex, overIndex)
    setTempState(prevState => ({ ...prevState, stages: reorderedStages }))
  }

  return (
    <Modal
      size={ModalSizeEnum.medium}
      opened={isOpened}
      afterLeave={afterLeave}
      isFetching={isUpdate}
      onClose={onClose}
      title={modalTitle}
    >
      <FormInput
        placeholder={t('global.name')}
        label={t('dashboard.crm_pipeline.modal.pipeline_name')}
        value={tempState?.name}
        onChange={e => setTempState(prev => prev && { ...prev, name: e.target.value })}
        className={'mb-8'}
        error={errors?.fields?.name}
      />
      <div>
        <div className={'mb-5 flex items-center justify-between'}>
          <span className={'text-text-primary text-[20px] font-semibold'}>
            {t('dashboard.crm_pipeline.modal.pipeline_statuses')}
          </span>
          <AddIcon
            className={`fill-brand-primary h-[24px] w-[24px] cursor-pointer`}
            onClick={() => createNewStage()}
          />
        </div>
        <div className={'customScroll max-h-[200px] overflow-y-auto md:max-h-[350px]'}>
          <Sortable
            draggable={getStageItem}
            items={tempState.stages.map(item => ({
              ...item,
              id: item.position,
            }))}
            onMove={(_, activeIndex, overIndex) => onMove(activeIndex, overIndex)}
            isPortal
          >
            {tempState.stages.map(getStageItem)}
          </Sortable>
        </div>
      </div>
      <div className={'mt-3 flex justify-end gap-3'}>
        <SecondaryButton disabled={isUpdate} onClick={onClose} width="large">
          {t('global.cancel')}
        </SecondaryButton>
        <PrimaryButton isFetching={isUpdate} onClick={saveCrmPipeline} width="large">
          {t('global.save')}
        </PrimaryButton>
      </div>
      <FieldErrorAndDescription
        error={errors?.common?.join(', ')}
        errorClassName={'text-center pt-2'}
      />
    </Modal>
  )
}
